import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  AutoAwesome,
  CalendarMonth,
  Schedule,
  Refresh,
  Download,
  Visibility,
  Save,
  Warning,
  CheckCircle,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { SelectChangeEvent } from '@mui/material/Select';
import { agendamentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Cliente {
  id: number;
  nome: string;
  dia_atendimento: string;
  hora_atendimento: string;
  duracao_sessao: number;
  ativo: boolean;
  id_usuario: number;
}

interface AgendamentoGerado {
  id: string;
  cliente_id: number;
  cliente_nome: string;
  data: string;
  dia_semana: string;
  hora: string;
  duracao: number;
  status: string;
  titulo_agendamento?: string;
  // Campos opcionais para agendamentos vindos do banco
  data_compromisso?: string;
  hora_compromisso?: string;
}

interface FormData {
  mes: number;
  ano: number;
}

const GerarAgenda: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });
  const [agendaGerada, setAgendaGerada] = useState<AgendamentoGerado[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [agendamentosExistentes, setAgendamentosExistentes] = useState<AgendamentoGerado[]>([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [totalRegistrosGravados, setTotalRegistrosGravados] = useState(0);

  useEffect(() => {
    // Obter usuário logado
    if (user) {
      carregarClientesComHorarios(user.id);
    } else {
      setError('Usuário não autenticado');
    }
  }, [user]);

  const carregarClientesComHorarios = async (idUsuario: number) => {
    try {
      console.log(`🔍 [DEBUG] Iniciando carregamento de clientes para usuário ID: ${idUsuario}`);
      const response: any = await agendamentoService.buscarClientesComHorarios(idUsuario);
      console.log(`🔍 [DEBUG] Resposta completa da API:`, response);
      console.log(`🔍 [DEBUG] Status da resposta:`, response.status);
      console.log(`🔍 [DEBUG] Data da resposta:`, response.data);
      
      if (response.success) {
        console.log(`✅ [DEBUG] Clientes carregados com sucesso: ${response.data.length} clientes`);
        setClientes(response.data);
      } else {
        console.error(`❌ [ERROR] Erro na resposta da API:`, response);
        setError('Erro ao carregar clientes: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error: any) {
      console.error('❌ [ERROR] Erro completo ao carregar clientes:', error);
      console.error('❌ [ERROR] Stack trace:', error.stack);
      console.error('❌ [ERROR] Response do erro:', error.response);
      setError('Erro de conexão ao carregar clientes: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleChange = (e: SelectChangeEvent<number>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const gerarAgendamentosParaCliente = (cliente: Cliente, mes: number, ano: number) => {
    const agendamentos: AgendamentoGerado[] = [];
    
    // Mapear dias da semana para números (0 = domingo, 1 = segunda, etc.)
    const diasSemanaMap: { [key: string]: number } = {
      'domingo': 0,
      'segunda-feira': 1,
      'segunda': 1,
      'terça-feira': 2,
      'terça': 2,
      'terca-feira': 2,
      'terca': 2,
      'quarta-feira': 3,
      'quarta': 3,
      'quinta-feira': 4,
      'quinta': 4,
      'sexta-feira': 5,
      'sexta': 5,
      'sábado': 6,
      'sabado': 6
    };

    const diaAtendimentoNormalizado = cliente.dia_atendimento.toLowerCase().trim();
    const diaSemanaNumero = diasSemanaMap[diaAtendimentoNormalizado];
    
    if (diaSemanaNumero === undefined) {
      console.warn(`Dia de atendimento não reconhecido para cliente ${cliente.nome}: ${cliente.dia_atendimento}`);
      return agendamentos;
    }

    console.log(`🔍 [DEBUG] Cliente ${cliente.nome}: ${cliente.dia_atendimento} = dia da semana ${diaSemanaNumero}`);

    // Encontrar todas as ocorrências desse dia da semana no mês
    const primeiroDiaDoMes = new Date(ano, mes - 1, 1);
    const ultimoDiaDoMes = new Date(ano, mes, 0).getDate();
    
    // Set para evitar duplicatas baseado em data+hora+cliente
    const agendamentosUnicos = new Set<string>();
    
    for (let dia = 1; dia <= ultimoDiaDoMes; dia++) {
      const dataAtual = new Date(ano, mes - 1, dia);
      
      // Se o dia da semana coincide com o dia de atendimento do cliente
      if (dataAtual.getDay() === diaSemanaNumero) {
        const dataFormatada = dataAtual.toLocaleDateString('pt-BR');
        const chaveUnica = `${cliente.id}-${dataFormatada}-${cliente.hora_atendimento}`;
        
        // Verificar se já existe agendamento para este cliente nesta data/hora
        if (!agendamentosUnicos.has(chaveUnica)) {
          agendamentosUnicos.add(chaveUnica);
          
          const tituloAgendamento = cliente.nome.substring(0, 12);

          agendamentos.push({
            id: `${cliente.id}-${dia}-${mes}-${ano}`,
            cliente_id: cliente.id,
            cliente_nome: cliente.nome,
            data: dataFormatada,
            dia_semana: dataAtual.toLocaleDateString('pt-BR', { weekday: 'long' }),
            hora: cliente.hora_atendimento,
            duracao: cliente.duracao_sessao,
            status: 'Agendado',
            titulo_agendamento: tituloAgendamento
          });
        } else {
          console.warn(`⚠️ [WARNING] Agendamento duplicado evitado: ${cliente.nome} em ${dataFormatada} às ${cliente.hora_atendimento}`);
        }
      }
    }

    console.log(`✅ [DEBUG] Cliente ${cliente.nome}: ${agendamentos.length} agendamentos únicos gerados`);
    return agendamentos;
  };

  const verificarAgendamentosExistentes = async () => {
    if (!user) return;
    
    // Gerar diretamente sem verificar agendamentos existentes
    procederComGeracao();
  };

  const procederComGeracao = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log(`🔍 [DEBUG] Gerando agenda para ${clientes.length} clientes`);
      const agendaTemp: AgendamentoGerado[] = [];
      
      // Para cada cliente ativo
      clientes.filter(cliente => cliente.ativo).forEach(cliente => {
        console.log(`🔍 [DEBUG] Processando cliente: ${cliente.nome}, dia_atendimento: ${cliente.dia_atendimento}`);
        
        // Gerar agendamentos para o cliente no mês/ano especificado
        const agendamentosCliente = gerarAgendamentosParaCliente(cliente, formData.mes, formData.ano);
        agendaTemp.push(...agendamentosCliente);
      });

      // Ordenar por data e hora
      agendaTemp.sort((a, b) => {
        const dataA = new Date(a.data.split('/').reverse().join('-') + 'T' + a.hora);
        const dataB = new Date(b.data.split('/').reverse().join('-') + 'T' + b.hora);
        return dataA.getTime() - dataB.getTime();
      });

      console.log(`✅ [DEBUG] Agenda gerada: ${agendaTemp.length} agendamentos`);
      setAgendaGerada(agendaTemp);
      setSuccess(`Agenda gerada com sucesso! ${agendaTemp.length} agendamentos criados.`);
      
      // Limpar mensagem de sucesso após 5 segundos
      setTimeout(() => {
        setSuccess('');
      }, 5000);
    } catch (error: any) {
      console.error('❌ [ERROR] Erro ao gerar agenda:', error);
      setError('Erro ao gerar agenda: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const gerarAgenda = () => {
    verificarAgendamentosExistentes();
  };

  const carregarAgendamentosGerados = async () => {
    // Função removida - usando geração local
  };

  const salvarAgendaNoBanco = async () => {
    if (!user || agendaGerada.length === 0) return;
    
    // Primeiro verificar se existem agendamentos no mês
    try {
      const response: any = await agendamentoService.buscarAgendamentosPorMes(
        formData.mes, 
        formData.ano, 
        user.id
      );
      
      if (response.success && response.data && response.data.length > 0) {
        setAgendamentosExistentes(response.data);
        setShowConfirmDialog(true);
      } else {
        procederComSalvamento();
      }
    } catch (error: any) {
      console.error('Erro ao verificar agendamentos:', error);
      procederComSalvamento(); // Continuar mesmo com erro na verificação
    }
  };

  const procederComSalvamento = async () => {
    if (!user || agendaGerada.length === 0) return;
    
    setLoading(true);
    setShowConfirmDialog(false);
    
    try {
      console.log(`🔍 [DEBUG] Iniciando salvamento de ${agendaGerada.length} agendamentos`);
      
      // SEMPRE deletar agendamentos do mês antes de criar novos
      console.log(`🔍 [DEBUG] Deletando TODOS os agendamentos do mês ${formData.mes}/${formData.ano} do usuário ${user.id}`);
      const deleteResponse = await agendamentoService.deletarAgendamentosMes(
        formData.mes, 
        formData.ano, 
        user.id
      );
      console.log(`✅ [DEBUG] Resposta da deleção:`, deleteResponse);

      // Converter agendamentos para o formato esperado pelo backend
      const agendamentosParaSalvar = agendaGerada.map(agendamento => ({
        data_compromisso: agendamento.data.split('/').reverse().join('-'), // Converter DD/MM/YYYY para YYYY-MM-DD
        hora_compromisso: agendamento.hora,
        duracao_minutos: agendamento.duracao,
        titulo_agendamento: agendamento.titulo_agendamento || agendamento.cliente_nome.substring(0, 12),
        id_cliente: agendamento.cliente_id,
        observacoes: '',
        tipo_sessao: 'Consulta'
      }));
      
      console.log(`🔍 [DEBUG] Agendamentos formatados para salvar:`, agendamentosParaSalvar);
      
      const response: any = await agendamentoService.criarAgendamentosMultiplos(
        agendamentosParaSalvar, 
        user.id
      );
      
      console.log(`🔍 [DEBUG] Resposta do salvamento:`, response);
      
      if (response.success) {
        console.log(`✅ [DEBUG] Salvamento realizado com sucesso: ${response.totalCriados || 0} registros`);
        setShowSuccessDialog(true);
        setTotalRegistrosGravados(response.totalCriados || 0);
      } else {
        console.error(`❌ [ERROR] Erro na resposta do salvamento:`, response);
        setError('Erro ao salvar agenda: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error: any) {
      console.error('❌ [ERROR] Erro completo ao salvar agenda:', error);
      console.error('❌ [ERROR] Stack trace:', error.stack);
      console.error('❌ [ERROR] Response do erro:', error.response);
      setError('Erro ao salvar agenda: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOverwrite = async () => {
    setShowConfirmDialog(false);
    if (!user) return;
    
    try {
      // Deletar agendamentos existentes do mês
      await agendamentoService.deletarAgendamentosMes(
        formData.mes,
        formData.ano,
        user.id
      );
      
      // Proceder com a geração
      procederComGeracao();
    } catch (error: any) {
      console.error('Erro ao deletar agendamentos:', error);
      setError('Erro ao deletar agendamentos existentes: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const limparAgenda = () => {
    setAgendaGerada([]);
    setSuccess('');
    setError('');
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  const anos = [];
  const anoAtual = new Date().getFullYear();
  for (let i = anoAtual - 1; i <= anoAtual + 2; i++) {
    anos.push(i);
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <AutoAwesome sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Gerar Agenda Automática
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Gere automaticamente a agenda mensal com base nos horários de atendimento cadastrados para cada cliente. Todos os agendamentos do mês selecionado serão removidos.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Formulário de Geração */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth color="primary" />
                Parâmetros da Agenda
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Mês</InputLabel>
                    <Select
                      name="mes"
                      value={formData.mes}
                      onChange={handleChange}
                      label="Mês"
                    >
                      {meses.map(mes => (
                        <MenuItem key={mes.value} value={mes.value}>
                          {mes.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ano</InputLabel>
                    <Select
                      name="ano"
                      value={formData.ano}
                      onChange={handleChange}
                      label="Ano"
                    >
                      {anos.map(ano => (
                        <MenuItem key={ano} value={ano}>
                          {ano}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={gerarAgenda}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesome />}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Gerando...' : 'Gerar Agenda'}
                  </Button>
                </Grid>

                {agendaGerada.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={salvarAgendaNoBanco}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                        sx={{ mb: 1 }}
                      >
                        {loading ? 'Salvando...' : 'Salvar no Banco'}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={limparAgenda}
                        startIcon={<Refresh />}
                      >
                        Limpar Agenda
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Resultado da Agenda */}
        <Grid item xs={12} md={8}>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {agendaGerada.length > 0 && (
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule color="primary" />
                    Agenda Gerada - {meses.find(m => m.value === formData.mes)?.label} {formData.ano}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Visualizar Agenda">
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Exportar Agenda">
                      <IconButton size="small">
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Dia da Semana</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Horário</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agendaGerada.map((agendamento) => (
                        <TableRow key={agendamento.id} hover>
                          <TableCell>{agendamento.data}</TableCell>
                          <TableCell>{agendamento.dia_semana}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{agendamento.hora}</TableCell>
                          <TableCell>{agendamento.cliente_nome}</TableCell>
                          <TableCell>
                            <Chip 
                              label={agendamento.status} 
                              size="small" 
                              color="success" 
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                    📊 Resumo: {agendaGerada.length} agendamentos gerados para {meses.find(m => m.value === formData.mes)?.label} de {formData.ano}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {agendaGerada.length === 0 && !loading && (
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <AutoAwesome sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  Nenhuma agenda gerada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione o mês e ano desejados e clique em "Gerar Agenda" para criar automaticamente os agendamentos.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Dialog de Confirmação */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Agendamentos Existentes
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Já existem <strong>{agendamentosExistentes.length} agendamentos</strong> para {meses.find(m => m.value === formData.mes)?.label} de {formData.ano}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deseja substituir todos os agendamentos existentes pela nova agenda gerada automaticamente?
          </Typography>
          
          {agendamentosExistentes.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Agendamentos que serão removidos:
              </Typography>
              {agendamentosExistentes.slice(0, 5).map((agendamento, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  • {agendamento.cliente_nome || 'Cliente'} - {new Date(agendamento.data_compromisso || agendamento.data).toLocaleDateString('pt-BR')} às {agendamento.hora_compromisso || agendamento.hora}
                </Typography>
              ))}
              {agendamentosExistentes.length > 5 && (
                <Typography variant="body2" color="text.secondary">
                  ... e mais {agendamentosExistentes.length - 5} agendamentos
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={procederComSalvamento} 
            variant="contained" 
            color="warning"
            startIcon={<Warning />}
          >
            Substituir Agendamentos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Sucesso */}
      <Dialog 
        open={showSuccessDialog} 
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'success.main', 
          color: 'success.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CheckCircle />
          Agenda Salva com Sucesso!
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box textAlign="center">
            <Typography variant="h6" sx={{ mb: 2 }}>
              {totalRegistrosGravados} agendamentos foram gravados no banco de dados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mês: {formData.mes}/{formData.ano}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => {
              setShowSuccessDialog(false);
              // Navegar para o dashboard
              window.location.href = '/app/dashboard';
            }}
            variant="contained" 
            color="success"
            size="large"
            sx={{ minWidth: 120 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GerarAgenda;
